import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useMatch } from '../context/MatchContext'

function formatOvers(balls) {
    const overs = Math.floor(balls / 6)
    const rem = balls % 6
    return `${overs}.${rem}`
}

function groupOvers(history) {
    const overs = {}
    history.forEach(h => {
        const over = h.over
        if (!overs[over]) overs[over] = []
        let symbol = ''

        if (h.extra) {
            if (h.extraType === 'wide') symbol = 'wd'
            else if (h.extraType === 'noball') symbol = 'nb'
            else if (h.extraType === 'bye') symbol = `${h.runs}b`
            else if (h.extraType === 'legbye') symbol = `${h.runs}lb`
        } else if (h.wicket) symbol = 'W'
        else symbol = h.runs?.toString() ?? '0'

        overs[over].push(symbol)
    })
    return overs
}


export default function Summary() {
    const navigate = useNavigate()
    const { state, resetMatch } = useMatch()
    const m = state.match

    if (!m) return <div className="bg-gray-800 p-4 rounded-2xl">No match to show</div>

    const a = m.scoreA?.runs || 0
    const b = m.scoreB?.runs || 0
    const aBalls = m.scoreA?.balls || 0
    const bBalls = m.scoreB?.balls || 0
    const aOvers = formatOvers(aBalls)
    const bOvers = formatOvers(bBalls)

    let result = m.result || ''
    if (m.finished && !result) {
        if (a > b) result = `${m.teamA.name} won by ${a - b} runs`
        else if (b > a) result = `${m.teamB.name} won by ${m.players.B.length - m.scoreB.wickets} wickets`
        else result = 'Match tied'
    }

    const crrA = aBalls > 0 ? (a / (aBalls / 6)).toFixed(2) : '0.00'
    const crrB = bBalls > 0 ? (b / (bBalls / 6)).toFixed(2) : '0.00'

    const target = m.target ?? null
    const runsNeeded = target ? Math.max(0, target - b) : null
    const ballsRemaining = target ? m.oversLimit * 6 - bBalls : null
    const rrr = target && ballsRemaining > 0
        ? (runsNeeded / (ballsRemaining / 6)).toFixed(2)
        : null

    // Bowling side = opposite of batting side for each innings
    const bowlingSideForA = m.players.B
    const bowlingSideForB = m.players.A

    return (
        <div className="space-y-6">
            {/* 🏏 Match Overview */}
            <div className="bg-gray-800 p-4 rounded-2xl shadow-md">
                <h2 className="text-lg font-semibold mb-2">Match Summary</h2>

                <div className="text-sm text-gray-300 space-y-1">
                    <div>{m.teamA.name}: {a}/{m.scoreA.wickets} ({aOvers} ov)</div>
                    <div>{m.teamB.name}: {b}/{m.scoreB.wickets} ({bOvers} ov)</div>
                    <div className="mt-2 font-medium text-accent">{result}</div>

                    {m.finished && (
                        <div className="mt-1 text-gray-400 text-sm">
                            {target && (
                                <>
                                    <div>Target: {target}</div>
                                    <div>CRR: {crrB} {rrr && `• RRR: ${rrr}`}</div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* 🏏 Team A Batting */}
            <section className="bg-gray-800 p-4 rounded-2xl shadow-md">
                <h3 className="font-medium mb-3">{m.teamA.name} Batting</h3>
                <table className="w-full text-sm text-gray-300">
                    <thead>
                        <tr className="text-gray-400">
                            <th className="text-left">Batter</th>
                            <th>R</th><th>B</th><th>4s</th><th>6s</th><th>SR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {m.players.A.map(p => {
                            const sr = p.balls > 0 ? ((p.runs / p.balls) * 100).toFixed(2) : '0.00'
                            return (
                                <tr key={p.id}>
                                    <td className="py-1">{p.name} {p.isOut ? '' : '*'}</td>
                                    <td className="text-center">{p.runs}</td>
                                    <td className="text-center">{p.balls}</td>
                                    <td className="text-center">{p.fours}</td>
                                    <td className="text-center">{p.sixes}</td>
                                    <td className="text-center">{sr}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </section>

            {/* 🏹 Team B Bowling */}
            <section className="bg-gray-800 p-4 rounded-2xl shadow-md">
                <h3 className="font-medium mb-3">{m.teamB.name} Bowling</h3>
                <table className="w-full text-sm text-gray-300">
                    <thead>
                        <tr className="text-gray-400">
                            <th className="text-left">Bowler</th>
                            <th>O</th><th>R</th><th>W</th><th>Econ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bowlingSideForA.map(b => {
                            const overs = (b.overs || 0) + ((b.balls || 0) / 6)
                            const econ = overs > 0 ? (b.runsConceded / overs).toFixed(2) : '0.00'
                            return (
                                <tr key={b.id}>
                                    <td className="py-1">{b.name}</td>
                                    <td className="text-center">{b.overs}.{b.balls || 0}</td>
                                    <td className="text-center">{b.runsConceded}</td>
                                    <td className="text-center">{b.wicketsTaken}</td>
                                    <td className="text-center">{econ}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </section>

            {/* 🏏 Team B Batting */}
            <section className="bg-gray-800 p-4 rounded-2xl shadow-md">
                <h3 className="font-medium mb-3">{m.teamB.name} Batting</h3>
                <table className="w-full text-sm text-gray-300">
                    <thead>
                        <tr className="text-gray-400">
                            <th className="text-left">Batter</th>
                            <th>R</th><th>B</th><th>4s</th><th>6s</th><th>SR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {m.players.B.map(p => {
                            const sr = p.balls > 0 ? ((p.runs / p.balls) * 100).toFixed(2) : '0.00'
                            return (
                                <tr key={p.id}>
                                    <td className="py-1">{p.name} {p.isOut ? '' : '*'}</td>
                                    <td className="text-center">{p.runs}</td>
                                    <td className="text-center">{p.balls}</td>
                                    <td className="text-center">{p.fours}</td>
                                    <td className="text-center">{p.sixes}</td>
                                    <td className="text-center">{sr}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </section>

            {/* 🏹 Team A Bowling */}
            <section className="bg-gray-800 p-4 rounded-2xl shadow-md">
                <h3 className="font-medium mb-3">{m.teamA.name} Bowling</h3>
                <table className="w-full text-sm text-gray-300">
                    <thead>
                        <tr className="text-gray-400">
                            <th className="text-left">Bowler</th>
                            <th>O</th><th>R</th><th>W</th><th>Econ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bowlingSideForB.map(b => {
                            const overs = (b.overs || 0) + ((b.balls || 0) / 6)
                            const econ = overs > 0 ? (b.runsConceded / overs).toFixed(2) : '0.00'
                            return (
                                <tr key={b.id}>
                                    <td className="py-1">{b.name}</td>
                                    <td className="text-center">{b.overs}.{b.balls || 0}</td>
                                    <td className="text-center">{b.runsConceded}</td>
                                    <td className="text-center">{b.wicketsTaken}</td>
                                    <td className="text-center">{econ}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </section>

            {/* 🧾 Over Summary */}
            <section className="bg-gray-800 p-4 rounded-2xl shadow-md">
                <h3 className="font-medium mb-3">Over Summary</h3>

                {(() => {
                    const overs = groupOvers(m.history || [])
                    const overNumbers = Object.keys(overs).sort((a, b) => Number(a) - Number(b))
                    if (overNumbers.length === 0)
                        return <div className="text-gray-400 text-sm">No overs bowled yet.</div>

                    return (
                        <div className="space-y-1">
                            {overNumbers.map(num => (
                                <div key={num} className="flex text-sm text-gray-300">
                                    <div className="w-20 text-gray-400">Over {num}:</div>
                                    <div className="flex flex-wrap gap-1">
                                        {overs[num].map((ball, i) => (
                                            <span
                                                key={i}
                                                className={`px-2 py-0.5 rounded-md ${ball === 'W'
                                                    ? 'bg-red-700 text-white'
                                                    : ball === '6'
                                                        ? 'bg-green-700 text-white'
                                                        : ball === '4'
                                                            ? 'bg-yellow-600 text-gray-900'
                                                            : 'bg-gray-700 text-gray-200'
                                                    }`}
                                            >
                                                {ball}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                })()}
            </section>


            {/* 🎬 Controls */}
            <div className="flex gap-2">
                <button
                    onClick={() => { resetMatch(); navigate('/') }}
                    className="flex-1 py-2 rounded-md bg-accent text-gray-900 font-semibold"
                >
                    Finish & Home
                </button>
                <button
                    onClick={() => navigate('/live')}
                    className="flex-1 py-2 rounded-md bg-gray-700"
                >
                    Back to Live
                </button>
            </div>
        </div>
    )
}
